import {
  Button,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import Add from "@material-ui/icons/Add";
import { Alert } from "@material-ui/lab";
import {Input, InputLabel, StyledSwitch } from "components/DSC";
import LoaderView from "components/LoaderView";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  getMultipleActivities,
  getPlanningData,
  saveCapacityPlanningDetails,
  saveCapacityPlanningToHistory,
} from "services/dsc";
import { EventEmitter } from "utils/EventEmitter";
import ActivityRow from "./ActivityRow";
import ManualActivityRow from "./ManualActivityRow";
import ClearDialog from "./ClearConfirmDialog";
import {
  days,
  initialDaysData,
  initialSelections,
  initialTotals,
  initialTotalsObj,
  defProductHours,
  defAPHTarget
} from "./constants";
import DropDowns from "./DropDowns";
import PlanDialog from "./PlanDialog";
import { useDSCStyles } from "./styles";
import {
  createActivityData,
  createDaysData,
  deepEqual,
  filterActs,
  findActivitiesWithPrimary,
  findParentDetails,
  formulas,
  getAverage,
  getEnabledDays,
  getTotals,
  groupByParent,
  isDayToggleOff,
  removeDataForDisabledDays,
  getTargetCapacity,
  deepArrayEqual,
  getDisplayName
} from "./utils";

import {isFractionalNumOnly} from 'utils/validations';
import { CSVLink } from 'react-csv';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import {analytics} from 'services/firebase';

export default function DSCView() {
  const { t } = useTranslation();
  const classes = useDSCStyles();
  const theme = useTheme();
  const { user } = useSelector((state) => state.user.auth);

  const { register, handleSubmit, control, setValue, getValues, reset, watch, formState:{errors} } =
    useForm({
      defaultValues: {
        weekTotal: initialTotals,
        daysData: initialDaysData,
      },
      // resolver: yupResolver(getValidationSchema()),
    });

  const daysData = watch("daysData");

  const [selections, setSelections] = React.useState(initialSelections);
  const [averageBy, setAverageBy] = React.useState({ name: "Monthly", id: "monthly" });
  const [activities, setActivities] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saveAllowed, setSaveAllowed] = React.useState(false);
  const [clearAllowed, setClearAllowed] = React.useState(false);
  const [cancelAllowed, setCancelAllowed] = React.useState(false);
  const [planExists, setPlanExists] = React.useState(false);
  const [clearDialogOpen, setClearDialogOpen] = React.useState(false);
  const [planDialogOpen, setPlanDialogOpen] = React.useState(false);
  const [docid, setDocId] = React.useState(null);
  const [oldData, setOldData] = React.useState(null);
  const [initialData, setInitialData] = React.useState(null);
  const [sessionData, setSessionData] = React.useState(null); // Storing session data between save

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [filledRows, setFilledRows] = useState([]);
  const noOfActivities = activities.length;
  const [target, setTarget] = useState(defAPHTarget);
  const [allActivities, setAllActivities] = useState([]);
  const [primaryBuildActivities, setPrimaryBuildActivites] = useState([]);
  const [existingPlans, setExistingPlans] = useState([]);
  const [primary, setPrimary] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [prevPlan, setPrevPlan] = useState(null);
  const [productHours, setProductHours] = useState(defProductHours);
  const [exportData, setExportData] = useState([]);
  const [dscActivityEnable, setDscActivityEnable] = useState(true);

  const handleCloseSnackbar = (event, reason) => {
    // if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  useEffect(()=>{
    if(existingPlans.length>0){
      setDocId(existingPlans[0].docId);
      loadPlan(existingPlans[0]);
    }
  },[existingPlans])

  useEffect(()=>{
    if(user.workerId){
      analytics.logEvent('imtime_dsc_page_visited',{
        userId:user?user.workerId:"",
        userName:user.firstName+" "+user.lastName,
        userCountry:user.country?user.country:""
      })
    }
  },[user])

  const doReset = () => {
    reset();
    setOldData(null);
    setSessionData(null);
    setCancelAllowed(false);
    setSaveAllowed(false);
    setClearAllowed(false);
    setPlanExists(false);
    setDocId(null);
  };

  const getDate = (dayNum=0) =>{
    return moment(selections.week).startOf("week").add(dayNum, "days").format("DD");
  }

  const exportHeader = [
    {key:'activityName', label:""},
    {key:'target', label:""},
    {key:'average', label:""},
    {key:'day1Count', label:`${t("other.dscLblSunday")} (${getDate(0)})`},
    {key:'day1Hours', label:""},
    {key:'day1Staff', label:""},
    {key:'day2Count', label:`${t("other.dscLblMonday")} (${getDate(1)})`},
    {key:'day2Hours', label:""},
    {key:'day2Staff', label:""},
    {key:'day3Count', label:`${t("other.dscLblTuesday")} (${getDate(2)})`},
    {key:'day3Hours', label:""},
    {key:'day3Staff', label:""},
    {key:'day4Count', label:`${t("other.dscLblWednesday")} (${getDate(3)})`},
    {key:'day4Hours', label:""},
    {key:'day4Staff', label:""},
    {key:'day5Count', label:`${t("other.dscLblThursday")} (${getDate(4)})`},
    {key:'day5Hours', label:""},
    {key:'day5Staff', label:""},
    {key:'day6Count', label:`${t("other.dscLblFriday")} (${getDate(5)})`},
    {key:'day6Hours', label:""},
    {key:'day6Staff', label:""},
    {key:'day7Count', label:`${t("other.dscLblSaturday")} (${getDate(6)})`},
    {key:'day7Hours', label:""},
    {key:'day7Staff', label:""},
    {key:'weekTotalCount', label:`${t("other.dscLblTotal")}`},
    {key:'weekTotalHours', label:""},
    {key:'weekTotalStaff', label:""},
  ]

  const getExportData = (event, done) =>{
    const values = getValues();
    console.log("values", values);
    let dataToExport = [
      {activityName:t("other.dscLblActivities"), target:t("other.dscLblTarget"), average:t("other.dscLblAverage"),
       day1Count:t("other.dscLblCount"), day1Hours:t("other.dscLblHours"), day1Staff:t("other.dscLblStaff"),
       day2Count:t("other.dscLblCount"), day2Hours:t("other.dscLblHours"), day2Staff:t("other.dscLblStaff"),
       day3Count:t("other.dscLblCount"), day3Hours:t("other.dscLblHours"), day3Staff:t("other.dscLblStaff"),
       day4Count:t("other.dscLblCount"), day4Hours:t("other.dscLblHours"), day4Staff:t("other.dscLblStaff"),
       day5Count:t("other.dscLblCount"), day5Hours:t("other.dscLblHours"), day5Staff:t("other.dscLblStaff"),
       day6Count:t("other.dscLblCount"), day6Hours:t("other.dscLblHours"), day6Staff:t("other.dscLblStaff"),
       day7Count:t("other.dscLblCount"), day7Hours:t("other.dscLblHours"), day7Staff:t("other.dscLblStaff"),
       weekTotalCount:t("other.dscLblCount"), weekTotalHours:t("other.dscLblHours"), weekTotalStaff:t("other.dscLblStaff")
      }
    ]

    try{
    //  activity Data to csv data
        const activityData = values?.activityData;
        const averages = values?.averages;
        console.log("values", activities, activityData)
        let isCurrentWeek = moment(selections.week).week()>=moment().week();
        activities && activities.length >0 && activityData && Object.keys(activityData).length>0 
        && activities.forEach(activity => {
            if( activity.parentId && activityData[activity.parentId]){
              let curActData = activity.id && activity.id != activity.parentId 
                                ? activityData[activity.id] : activityData[activity.parentId];
              let dataObj = {
                activityName : getDisplayName(activity,t),
                target : getTargetCapacity(activity, target),
                average : isCurrentWeek? getAverage(activity, averageBy, primaryBuildActivities):averages?.[activity.id]?averages[activity.id]:0
              }
              curActData && Object.keys(curActData).length >0 && Object.keys(curActData).forEach(day =>{
                dataObj[`${day}Count`] = curActData[day].activityCount ? curActData[day].activityCount : "";
                dataObj[`${day}Hours`] = curActData[day].hours ? curActData[day].hours : "";
                dataObj[`${day}Staff`] = curActData[day].staff ? curActData[day].staff : "";
              })
              dataToExport.push(dataObj);
            }
        })

        // custom Act Data to csv
        const customActData = values?.customActData
        customActData && customActData.length>0 && customActData.forEach(custAct =>{
          if(custAct.activityName && custAct.activityTarget){
          let dataObj = {
            activityName : custAct.activityName? custAct.activityName:"",
            target : custAct.activityTarget? custAct.activityTarget : "",
            average : custAct.activityAverage? custAct.activityAverage : ""
          }
          let dayPlan = custAct?.dayPlan;
          dayPlan && Object.keys(dayPlan).length >0 && Object.keys(dayPlan).forEach(day =>{
            dataObj[`${day}Count`] = dayPlan[day].activityCount ? dayPlan[day].activityCount : "";
            dataObj[`${day}Hours`] = dayPlan[day].hours ? dayPlan[day].hours : "";
            dataObj[`${day}Staff`] = dayPlan[day].staff ? dayPlan[day].staff : "";
          }) 
          dataToExport.push(dataObj);
        }
        })

        // adding totals row to export
         const weekTotal = values?.weekTotal;
         if(weekTotal && Object.keys(weekTotal).length>0){
          let dataObj = {
            activityName : "Total",
            target :  "",
            average : ""
          }
          Object.keys(weekTotal).forEach(day =>{
            dataObj[`${day}Count`] = weekTotal[day].totalActivityCount ? weekTotal[day].totalActivityCount : "";
            dataObj[`${day}Hours`] = weekTotal[day].totalHours ? weekTotal[day].totalHours : "";
            dataObj[`${day}Staff`] = weekTotal[day].totalStaff ? weekTotal[day].totalStaff : "";
           })
           dataToExport.push({});
           dataToExport.push(dataObj);
         }

          console.log("values", dataToExport)
        setExportData(dataToExport);
        done(true);
      }catch(err){
        console.log("error", err);
        done(false);
      }
  }

  const onProductHrsChanged = (workHours) =>{
    if (averageBy && noOfActivities > 0 && workHours) {
      const values = getValues();
      const custumActivities = values.customActData;
      const dayValues = values.daysData;
      
      for (let activity of activities) {
        for (let i = 0; i < 7; ++i) {
          const d = `day${i + 1}`;
          const enabledDays = getEnabledDays(dayValues);
          if (enabledDays.includes(d)) {
            const activityCount = values["activityData"][`${activity.id}`][`${d}`]["activityCount"];
            const value = parseInt(activityCount ? activityCount : "0");
            const targetCapacity = getTargetCapacity(activity, target);
            const staff = formulas.getStaff(targetCapacity, value, workHours);
            setValue(
              `activityData.${activity.id}.${d}.staff`,
              value ? staff : ""
            );
          }
        }
      }

      custumActivities && custumActivities.length>0 && custumActivities.forEach((custAct, index) =>{
        for (let i = 0; i < 7; ++i) {
          const d = `day${i + 1}`;
          const enabledDays = getEnabledDays(dayValues);
          if (enabledDays.includes(d)) {
            const activityCount = custAct["dayPlan"][`${d}`]["activityCount"];
            const value = parseInt(activityCount ? activityCount : "0");
            const targetCapacity = custAct["activityTarget"];
            const staff = formulas.getStaff(targetCapacity, value, workHours);
            setValue(
              `customActData[${index}].dayPlan.${d}.staff`,
              value && targetCapacity ? staff : ""
            );
          }
        }
      })

      onUpdate(workHours);
    }
  }

  const addActivitiesToView = (primarySelection) => {
    const activityList = primarySelection.activities;
    if (!Array.isArray(activityList) || activityList.length == 0) {
      console.log("No Activities");
      setActivities([]);
      return;
    }

    //const parentsMap = groupByParent(activityList);

    // console.log("Activities map", parentsMap);

    // const parentActivities = findParentDetails(
    //   Object.keys(parentsMap),
    //   activityList
    // );

    // console.log("Activities parents", parentActivities);
    // const acts = addDemoAverages(filterActs(data));
    const acts = filterActs(activityList);
    // console.log("Activities", acts);
    // Creating a defaultInitialValue
    let obj = createActivityData(acts);
    let dayObj = createDaysData();
    setInitialData({
      activityData: obj,
      weekTotal: initialTotalsObj,
      daysData: dayObj,
    });
    setOldData({
      activityData: obj,
      weekTotal: initialTotalsObj,
      daysData: dayObj,
    });
    setSessionData({
      activityData: obj,
      weekTotal: initialTotalsObj,
      daysData: dayObj,
    });
    setActivities(acts);
    return { acts, obj };
  };

  async function fetchPlanningData(primary) {
    console.log("fetchPlanningData", primary);
    setLoading(true);
    if (!primary || !selections.district || !selections.week) {
      return;
    }

    try {
      const plans = await getPlanningData(
        selections.district,
        primary,
        selections.week
      );
      setExistingPlans(plans);
      console.log("fetchPlanningData Data", plans);
    } catch (e) {
      setExistingPlans([]);
      console.log(e);
    }
    setLoading(false);
  }

  const loadPlan = (data) => {
    const { docId, plan } = data;
    setPlanExists(true);
    setDocId(docId);
    setValue("weekTotal", plan.weekTotal);
    if (plan.daysData) setValue("daysData", plan.daysData);
    let savedActivityData = plan.activityData;
    // Removed unused keys
    Object.keys(savedActivityData).forEach((key) => {
      if (activities.find((a) => a.id == key) == undefined) {
        delete savedActivityData[key];
      }
    });
    console.log("DSC plan exists", savedActivityData);
    console.log("DSC activities", activities);
    setValue("activityData", savedActivityData);
    if(plan.averages && Object.keys(plan.averages).length>0){
      setValue("averages", plan.averages);
    }else{
      setValue("averages", {});
    }
    if(plan.customActData && plan.customActData.length>0){
      setValue("customActData", plan.customActData);
    }else{
      setValue("customActData", []);
    }
    plan.productivityHrs && setProductHours(Number(plan.productivityHrs));
    setOldData(plan);
    setSessionData(plan);
    setClearAllowed(true);
    setSaveAllowed(false);
    if(!plan.weekTotal.weekTotal){
      setTimeout(()=>{
        onUpdate(plan?.productivityHrs);
      },0)
    }
  };

  async function fetchData() {
    try {
      console.log("fetchData getting called");
      analytics.logEvent('imtime_dsc_plan_viewed',{
        userId:user?user.workerId:"",
        userName:user.firstName+" "+user.lastName,
        userCountry:user.country?user.country:"",
        districtSelected:selections.district
      })
      doReset();
      setLoading(true);
      const activityResponse = await getMultipleActivities(
        selections.district,
        selections.buildings
      );
      setAllActivities(activityResponse);
      console.log("fetchData Activities", activityResponse);
      if (primary) {
        console.log("fetchData primary", primary);
        const primarySelection = findActivitiesWithPrimary(
          primary,
          activityResponse
        );
        setPrimaryBuildActivites(primarySelection);
        const { acts, obj } = addActivitiesToView(primarySelection);
        console.log("Acts2", acts, obj);
      }
    } catch (e) {
      setActivities([]);
    }
    setLoading(false);
  }

  const handleLoadClick = async () => {
    doReset();
    setExistingPlans([]);
    EventEmitter.dispatch("clearSelectedPlan", { prevPlan: null });
    if (!primary) {
      setActivities([]);
      setLoading(false);
      return;
    }
    if (selections.week && selections.district && selections.buildings) {
      await fetchData();
      if (primary) await fetchPlanningData(primary);
    }
  };

  const onDayToggled = () => {
    if (averageBy && noOfActivities > 0) {
      console.log("onDayToggled");
      const values = getValues();
      const dayValues = values.daysData;
      const latestFilledRows = [];
      for (let activity of activities) {
        // check if this activity was filled earlier, if yes then remove filled values
        if (filledRows.includes(activity.id) && !planExists) {
          for (let i = 0; i < 7; ++i) {
            const d = `day${i + 1}`;
            setValue(`activityData.${activity.id}.${d}.activityCount`, "");
            setValue(`activityData.${activity.id}.${d}.hours`, "");
            setValue(`activityData.${activity.id}.${d}.staff`, "");
          }
        }
        const averages = values?.averages;
        let isCurrentWeek = moment(selections.week).week()>=moment().week();
        const average = isCurrentWeek? getAverage(activity, averageBy, primaryBuildActivities):averages?.[activity.id]?averages[activity.id]:0;
        // if(!average) continue; // Keeps existing values when the selected average types doesn't have value
        const enabledDays = getEnabledDays(dayValues);
        const avgToFill = average
          ? Math.round(parseFloat(average) / enabledDays.length)
          : average;
        if (avgToFill) {
          latestFilledRows.push(activity.id);
        }
        for (let i = 0; i < 7; ++i) {
          const d = `day${i + 1}`;
          if (enabledDays.includes(d) && avgToFill && !planExists) {
            // avgToFill checks whether there is any value to fill
            setValue(
              `activityData.${activity.id}.${d}.activityCount`,
              avgToFill
            );
            const value = parseInt(avgToFill ? avgToFill : "0");
            const targetCapacity = getTargetCapacity(activity, target);
            const hours = formulas.getHours(targetCapacity, value);
            const staff = formulas.getStaff(targetCapacity, value, productHours);
            setValue(
              `activityData.${activity.id}.${d}.hours`,
              value ? hours : ""
            );
            setValue(
              `activityData.${activity.id}.${d}.staff`,
              value ? staff : ""
            );
          } else {
            // Not removing values when the column is disabled
            // setValue(`activityData.${activity.id}.${d}.activityCount`, "");
            // setValue(`activityData.${activity.id}.${d}.hours`, "");
            // setValue(`activityData.${activity.id}.${d}.staff`, "");
          }
        }
      }
      setFilledRows(latestFilledRows);
      onUpdate();
    }
  };

  const clearAllRowData = () => {
    for (let activity of activities) {
      for (let i = 0; i < 7; ++i) {
        const d = `day${i + 1}`;
        setValue(`activityData.${activity.id}.${d}.activityCount`, "");
        setValue(`activityData.${activity.id}.${d}.hours`, "");
        setValue(`activityData.${activity.id}.${d}.staff`, "");
      }
    }
  };

  React.useEffect(() => {
    if (averageBy && noOfActivities > 0) {
      onDayToggled();
      if(!planExists){
        setCancelAllowed(false);
        let {activityData, daysData ,weekTotal} = getValues();
        setSessionData({...getValues()});
        setOldData({...getValues()});
      }
    }
  }, [averageBy, noOfActivities]);

  const handleSubmitAction = async (input) => {
    console.log(input);
    analytics.logEvent('imtime_dsc_plan_saved',{
      userId:user?user.workerId:"",
      userName:user.firstName+" "+user.lastName,
      userCountry:user.country?user.country:"",
      districtSelected:selections.district
    })
    if (!primary || selections.buildings.length == 0) return;

    const afterRemoval = removeDataForDisabledDays(input);
    let dataToSubmit = {
      ...afterRemoval,
      districtId: selections.district,
      buildingIds: selections.buildings,
      primaryBuilding: primary,
      weekStartDate: selections.week,
      planName: `${selections.district} ${primary} ${selections.week}`,
      actionBy: user.workerId,
      actionTS: moment().format(),
      version: 1,
      productivityHrs:productHours
    };

    console.log(dataToSubmit);
    // setLoading(false);
    // return;
    setLoading(true);
    try {
      if (planExists && docid) {
        await saveCapacityPlanningToHistory(dataToSubmit, oldData, docid);
        console.log("Updated Document Id", docid);
      } else {
        const docId = await saveCapacityPlanningDetails(dataToSubmit);
        console.log("Saved Document Id", docId);
        setPlanExists(true);
        setDocId(docId);
      }
      setLoading(false);
      setOldData(dataToSubmit);
      setSessionData(dataToSubmit);
      let savedActivityData = dataToSubmit.activityData;
      // Removed unused keys
      Object.keys(savedActivityData).forEach((key) => {
        if (activities.find((a) => a.id == key) == undefined) {
          delete savedActivityData[key];
        }
      });
      setValue("activityData", savedActivityData);
      setValue("weekTotal", dataToSubmit.weekTotal);
      if(dataToSubmit.customActData && dataToSubmit.customActData.length>0){
        setValue("customActData", dataToSubmit.customActData);
      }else{
        setValue("customActData", []);
      }
      setSaveAllowed(false);
      setCancelAllowed(false);
      setOpenSnackbar(true);
    } catch (e) {
      setLoading(false);
    }
  };

  const onUpdate = (workHours) => {
    console.log("onUpdate called");
    const activityData = getValues().activityData;
    const daysData = getValues().daysData;
    // get manual Activities data entered by user and update weekTotal
    const customActData = getValues().customActData;
    console.log("check3", customActData);
    const manActObj = {};
    customActData && customActData.length>0 && customActData.forEach((manAct, ind) =>{
      if(manAct.activityName && manAct.activityName !=="" && manAct.activityTarget && manAct.activityTarget !==""){
      manActObj[`man_act${ind}`] = manAct.dayPlan;
      }
    })
     
    const { totalValue, totals, actWeekTotals } = getTotals({...activityData,...manActObj});
    Object.keys(actWeekTotals).length>0 && Object.keys(actWeekTotals).forEach(act_id => {
      if(activityData[act_id]){
        setValue(`activityData.${act_id}.weekTotal.activityCount`, actWeekTotals[act_id].totalActivityCount);
        setValue(`activityData.${act_id}.weekTotal.hours`, actWeekTotals[act_id].totalHours);
        setValue(`activityData.${act_id}.weekTotal.staff`, actWeekTotals[act_id].totalStaff);
      }else if(act_id.includes("man_act")){
        let custInd = act_id.split('man_act')[1];
        if(custInd && !isNaN(custInd)){
          setValue(`customActData[${custInd}].dayPlan.weekTotal.activityCount`, actWeekTotals[act_id].totalActivityCount);
        setValue(`customActData[${custInd}].dayPlan.weekTotal.hours`, actWeekTotals[act_id].totalHours);
        setValue(`customActData[${custInd}].dayPlan.weekTotal.staff`, actWeekTotals[act_id].totalStaff);
        }
      }
    })
    setValue("weekTotal", totals);
    const check1 = !deepEqual(oldData.activityData, activityData);
    const check2 = !deepEqual(oldData.daysData, daysData);
    const check3 = !deepArrayEqual(oldData.customActData, customActData);
    console.log("check3", check3, check2, check1);
    // console.log(
    //   "onUpdate total, check1, check2",
    //   totalValue,
    //   check1,
    //   check2,
    //   oldData.activityData,
    //   activityData
    // );
    if (oldData != null) {
      // if data has changed then enabled save button(no matter if the toggles were changed or not)
      if (check1 || check3) {
        console.log("Check 1");
        setSaveAllowed((check1 && totalValue > 0) || check3);
      } else {
        // if data has not changed then
        // if the toggles have changed then
        if (check2) {
          console.log("Check 2");
          // if the total>0 then enable save
          if (totalValue > 0) {
            setSaveAllowed(check2 && totalValue > 0);
          } else {
            // else disable save
            console.log("Total < 0, thus disabling");
            setSaveAllowed(false);
          }
        } else {
          // If the toggles have not changed then disable save.
          console.log("No toggle change, thus disabling");
          if(planExists && workHours && sessionData.productivityHrs && sessionData.productivityHrs != workHours){
            setSaveAllowed(true);
          }else if(!planExists && workHours && defProductHours !== workHours){
            setSaveAllowed(true);
          }else{
            setSaveAllowed(false);
          }
        }
      }
    }
    if (sessionData != null)
      setCancelAllowed(!deepEqual(sessionData.activityData, activityData));
    if (initialData != null)
      setClearAllowed(!deepEqual(initialData.activityData, activityData) || !deepArrayEqual(initialData.customActData, customActData));
  };

  const handleSelectionsChange = (selection) => {
    setSelections(selection);
    console.log(selection);
  };

  const handleAverageChange = (average) => {
    //setAverageBy(average);
  };

  const handlePrimaryChange = async (newPrimary) => {
    console.log("handlePrimaryChange", newPrimary);
    doReset();
    setExistingPlans([]);
    setPrimary(newPrimary);
  };

  React.useEffect(() => {
    // if (primary) {
    //   const p = findActivitiesWithPrimary(primary, allActivities);
    //   if (p != undefined) addActivitiesToView(p);
    //   else {
    //     setActivities([]);
    //     setSaveAllowed(false);
    //   }
    // } else {
    //   setActivities([]);
    //   setSaveAllowed(false);
    // }
    if(selections.week)
    {
      let currentWeekTuesday = moment(selections.week).day("Tuesday").format('YYYY-MM-DD');
      let lastWeekMonday = moment(selections.week).subtract(1,'week').day("Monday").format('YYYY-MM-DD');
      let currentDay = moment().format('YYYY-MM-DD');
      if(currentDay > lastWeekMonday && currentDay <= currentWeekTuesday )
      {
        setDscActivityEnable(false);
      }else{
        setDscActivityEnable(true);
      }
     }

    setActivities([]);
    setProductHours(defProductHours);
    setSaveAllowed(false);
    handleLoadClick();
    // clearAllRowData();
  }, [primary, selections.week]);

  const handlePlanChange = async (newPlan) => {
    setPrevPlan(selectedPlan);
    if (!newPlan) {
      setSelectedPlan(null);
      reset();
      return;
    }
    setSelectedPlan(newPlan);
    setPlanDialogOpen(true);
  };

  const handleClear = () => {
    setClearDialogOpen(true);
  };

  const handleCancel = () => {
    console.log("clicked on cancel");
    console.log("Session", sessionData);
    if (sessionData != null) {
      if (sessionData.activityData)
        setValue("activityData", sessionData.activityData);
        if (sessionData.customActData) setValue("customActData", sessionData.customActData);
      if (sessionData.weekTotal) setValue("weekTotal", oldData.weekTotal);
      // if (sessionData.daysData)
      setValue("daysData", oldData.daysData);
    }
    setCancelAllowed(false);
    if (planExists) setClearAllowed(true);
    if (planExists) {
      sessionData.productivityHrs && setProductHours(Number(sessionData.productivityHrs));
    }else{
      setProductHours(defProductHours);
    }
    if(deepEqual(sessionData.activityData,initialData.activityData)|| planExists){
      setSaveAllowed(false);
    }
  };

  const handleDialogClose = (accepted) => {
    if (accepted) {
      let {customActData} = getValues();
      let formattedCustActData = customActData && customActData.length>0 ? customActData.map(act =>{
        return {
          ...act,
          dayPlan:{...initialTotalsObj}
        }
      }):[]
      reset();
      setValue('customActData',formattedCustActData);
      setClearAllowed(false);
      if (planExists) {
        setSaveAllowed(true);
        setCancelAllowed(true);
      } else {
        setSaveAllowed(false);
        setCancelAllowed(false);
      }
      console.log("you have accepted clear");
    }
    setClearDialogOpen(false);
  };

  // Subscriptions to district clear
  useEffect(() => {
    EventEmitter.subscribe("districtChangeOrNone", () => {
      console.log("districtChangeOrNone");
      doReset();
      setActivities([]);
      setExistingPlans([]);
      setSelectedPlan(null);
      setPrimary(null);
      setAllActivities([]);
      setPrimaryBuildActivites([]);
    });
  }, []);

  const found = days
    .map((item) => {
      switch (item) {
        case "SUNDAY":
          return (item = t("other.dscLblSunday"));
        case "MONDAY":
          return (item = t("other.dscLblMonday"));
        case "TUESDAY":
          return (item = t("other.dscLblTuesday"));
        case "WEDNESDAY":
          return (item = t("other.dscLblWednesday"));
        case "THURSDAY":
          return (item = t("other.dscLblThursday"));
        case "FRIDAY":
          return (item = t("other.dscLblFriday"));
        case "SATURDAY":
          return (item = t("other.dscLblSaturday"));
        default:
          item = "new";
      }
    })
    .map((e) => e.toUpperCase());

  //console.log(".....................you're here.............",found)

  return (
    <div className={classes.root}>
      <LoaderView loading={loading}>
        <div style={{display:'flex', alignItems:'end', justifyContent:'space-between'}}>
        <DropDowns
          onChangeSelections={handleSelectionsChange}
          //onChangeAverage={handleAverageChange}
          onChangePrimary={handlePrimaryChange}
          onClickLoad={handleLoadClick}
          loading={loading}
          setLoading={setLoading}
          existingPlans={existingPlans}
          onChangePlan={handlePlanChange}
        />
                 {activities.length > 0 &&      
                          <CSVLink
                            data={exportData}
                            asyncOnClick={true}
                            onClick={getExportData}
                            headers={exportHeader}
                            filename={`${primary}_DSC_Plan_${moment().format("DD-MM-YYYY")}`}
                          >
                            {/* <CloudDownloadIcon style={{cursor:'pointer', color:"#1B75BC", padding:'0 20px', fontSize:'28px'}} /> */}
                            <Button
                              variant="contained"
                              color="primary"
                            >
                             {t("other.lblBtnExportButton")}
                            </Button>
                        </CSVLink>}
                        </div>
        <form
          noValidate
          className={classes.content}
          onSubmit={handleSubmit(handleSubmitAction)}
        >
          {activities.length > 0 && (
            <React.Fragment>
              <TableContainer
                component={Paper}
                className={classes.tableContainer}
              >
                <Table
                  aria-label="collapsible table"
                  size="small"
                  padding="default"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        //className={classes.stickHeader}
                        className={classes.thRow}
                        align="right"
                      >
                    </TableCell>
                      <TableCell
                        className={classes.stickHeader}
                        align="center"
                        padding="none"
                        style={{ paddingLeft: "0px", paddingRight: "4px",rowGap:"5px" }}
                        colSpan={2}
                      >
                        <Typography
                          variant="body1"
                          component="h6"
                          style={{ fontSize: "12px", marginBottom:"5px" }}
                        >
                          {t("other.workHours")}
                        </Typography>
                        <Input className={classes.productHrInp}
                          onChange={(e) =>{
                            let val = e.target.value;
                            if(isFractionalNumOnly(val) && Number(val)<=24){
                              setProductHours(val);
                              Number(val)>0 && onProductHrsChanged(val);
                            }
                          }}
                          value={productHours}
                          onBlur = {() => {
                            if(Number(productHours) <= 0 || Number(productHours) > 24){
                              setProductHours(defProductHours);
                              onProductHrsChanged(defProductHours);
                            }
                          }}
                          type="number" step=".01" min={0} max={24}/>
                        </TableCell>
                      {found.map((day, i) => (
                        <React.Fragment key={"h" + day + i}>
                          <TableCell
                            // style={{ padding:"0px 16px 0px 24px" }}
                            className={classes.stickHeader}
                            colSpan={3}
                            align="left"
                            style={{
                              paddingLeft: theme.spacing(5),
                              paddingRight: theme.spacing(1),
                            }}
                          >
                            <Grid container spacing={2}>
                              <Grid item>
                                <Grid
                                  container
                                  direction="column"
                                  alignItems="center"
                                  justify="center"
                                >
                                  <Grid item>
                                    <Typography
                                      variant="body1"
                                      component="h6"
                                      style={{
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {day.substring(0, 3)}
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    <Typography
                                      component="div"
                                      variant="subtitle2"
                                      className={classes.colDate}
                                    >
                                      {moment(selections.week)
                                        .startOf("week")
                                        .add(i, "days")
                                        .format("DD")}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item>
                                <Grid container alignItems="center" spacing={0}>
                                  <Grid item>
                                    <Controller
                                      control={control}
                                      defaultValue={true}
                                      render={({ onChange, ref, value }) => (
                                        <StyledSwitch
                                          size="small"
                                          checked={value}
                                          inputRef={ref}
                                          disabled={dscActivityEnable}
                                          onChange={(e, checked) => {
                                            onChange(checked);
                                            onDayToggled();
                                            const daysData =
                                              getValues().daysData;
                                            const daysChanged = !deepEqual(
                                              oldData.daysData,
                                              daysData
                                            );
                                            if (!daysChanged) {
                                              setSaveAllowed(false);
                                            } else {
                                              onUpdate();
                                            }
                                          }}
                                        />
                                      )}
                                      name={`daysData.day${i + 1}.enabled`}
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Tooltip
                                      placement="top"
                                      title={
                                        "Include/Exclude column data from plan."
                                      }
                                    >
                                      <InfoIcon style={{ fontSize: 16 }} />
                                    </Tooltip>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </TableCell>
                        </React.Fragment>
                      ))}
                       <TableCell style={{paddingLeft:'40px'}} className={classes.stickHeader} colSpan={3}>  {t("other.lblTotal")} </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        className={classes.thRow}
                        align="left"
                        component="th"
                        scope="row"
                      >
                        <Typography
                          variant="body1"
                          component="h6"
                          style={{ fontSize: "12px", paddingLeft:'40px' }}
                        >
                          {t("other.dscLblActivities")}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.stickHeader2}>
                        <Typography
                          variant="body1"
                          component="h6"
                          style={{ fontSize: "12px" }}
                        >
                         
                          {t("other.lblTarget")}
                        </Typography>
                      </TableCell>
                      {/* <TableCell
                        className={classes.stickHeader}
                        align="center"
                        style={{ paddingLeft: "4px", paddingRight: "0px" }}
                      >
                        <Typography
                          variant="body1"
                          component="h6"
                          style={{ fontSize: "12px" }}
                        >
                          @100%
                        </Typography>
                      </TableCell> */}
                      <TableCell
                        className={classes.stickHeader}
                        align="center"
                        style={{ paddingLeft: "4px", paddingRight: "0px" }}
                      >
                        <Typography
                          variant="body1"
                          component="h6"
                          style={{ fontSize: "12px" }}
                        >
                          {t("other.dscLblAverage")}
                        </Typography>
                      </TableCell>
                      {found.map((day) => (
                        <React.Fragment key={day}>
                          <TableCell
                            className={classes.countField}
                            align="center"
                            padding="none"
                            style={{
                              paddingLeft: theme.spacing(3),
                              paddingRight: theme.spacing(1),
                            }}
                          >
                            <Typography
                              scope="row"
                              style={{ fontSize: "12px" }}
                            >
                              {t("other.dscLblCount")}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={classes.stickHeader2}
                            align="center"
                            padding="none"
                          >
                            <Typography
                              scope="row"
                              style={{ fontSize: "12px" }}
                            >
                              {t("other.dscLblHours")}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={classes.stickHeader2}
                            align="center"
                            padding="none"
                          >
                            <Typography
                              scope="row"
                              style={{ fontSize: "12px" }}
                            >
                              {t("other.dscLblStaff")}
                            </Typography>
                          </TableCell>
                        </React.Fragment>
                      ))}
                      <React.Fragment key="total">
                          <TableCell
                            className={classes.countField}
                            align="center"
                            padding="none"
                            style={{
                              paddingLeft: theme.spacing(3),
                              paddingRight: theme.spacing(1),
                            }}
                          >
                            <Typography
                              scope="row"
                              style={{ fontSize: "12px" }}
                            >
                              {t("other.dscLblCount")}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={classes.stickHeader2}
                            align="center"
                            padding="none"
                          >
                            <Typography
                              scope="row"
                              style={{ fontSize: "12px" }}
                            >
                                {t("other.dscLblHours")}
                            </Typography>
                          </TableCell>
                          <TableCell
                            className={classes.stickHeader2}
                            align="center"
                            padding="none"
                          >
                            <Typography
                              scope="row"
                              style={{ fontSize: "12px" }}
                            >
                              {t("other.dscLblStaff")}
                            </Typography>
                          </TableCell>
                        </React.Fragment>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activities.map((activity, index) => (
                      <ActivityRow
                        register={register}
                        activityId={index}
                        key={index}
                        activity={activity}
                        control={control}
                        setValue={setValue}
                        averageBy={averageBy}
                        planExists={planExists}
                        target={target}
                        onUpdate={() => onUpdate()}
                        productHours={productHours}
                        primaryBuildActivities={primaryBuildActivities}
                        primaryBuilding={primary}
                        dscActivityEnable = {dscActivityEnable}
                        selections={selections}
                      />
                    ))}
                    
                       <ManualActivityRow
                        register={register}
                        setValue={setValue}
                        planExists={planExists}
                        control={control}
                        onUpdate={() => onUpdate()}
                        productHours={productHours}
                        getValues = {getValues}
                        dscActivityEnable = {dscActivityEnable}
                        errors={errors}
                      />

                  </TableBody>
                  <TableFooter>
                  <TableRow>
                </TableRow>
                    <TableRow>
                      <TableCell
                        className={classes.thRow}
                        component="th"
                        scope="row"
                      >
                        <Typography
                          variant="body1"
                          component="h6"
                          style={{ fontWeight: "bold" }}
                        >
                          {t("other.lblTotal")}
                        </Typography>
                      </TableCell>
                      {/* <TableCell className={classes.stickFooter} /> */}
                      <TableCell className={classes.stickFooter} />
                      <TableCell className={classes.stickFooter} />
                      {found.map((day, dayIndex) => (
                        <React.Fragment key={day + dayIndex}>
                          <TableCell
                            className={classes.stickFooter}
                            align="center"
                            style={{
                              paddingLeft: theme.spacing(3),
                              paddingRight: theme.spacing(1),
                            }}
                          >
                            <InputLabel
                              fullWidth
                              readOnly
                              margin="dense"
                              disabled={isDayToggleOff(
                                daysData,
                                `day${dayIndex + 1}`
                              )}
                              inputRef={register()}
                              name={`weekTotal.day${
                                dayIndex + 1
                              }.totalActivityCount`}
                              defaultValue={"0"}
                              inputProps={{
                                style: { color: "white", border: "unset" },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            className={classes.stickFooter}
                            align="center"
                            padding="none"
                          >
                            <InputLabel
                              fullWidth
                              readOnly
                              margin="dense"
                              disabled={isDayToggleOff(
                                daysData,
                                `day${dayIndex + 1}`
                              )}
                              inputRef={register()}
                              name={`weekTotal.day${dayIndex + 1}.totalHours`}
                              defaultValue={"0.0"}
                              className={classes.hrsField}
                              inputProps={{
                                style: { color: "white", border: "unset" },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            className={classes.stickFooter}
                            align="center"
                            padding="none"
                          >
                            <InputLabel
                              fullWidth
                              readOnly
                              margin="dense"
                              disabled={isDayToggleOff(
                                daysData,
                                `day${dayIndex + 1}`
                              )}
                              inputRef={register()}
                              className={classes.staffField}
                              name={`weekTotal.day${dayIndex + 1}.totalStaff`}
                              defaultValue={"0.0"}
                              inputProps={{
                                style: { color: "white", border: "unset" },
                              }}
                            />
                          </TableCell>
                        </React.Fragment>
                      ))}
                        <React.Fragment key="totalWeek">
                          <TableCell
                            className={classes.stickFooter}
                            align="center"
                            style={{
                              paddingLeft: theme.spacing(3),
                              paddingRight: theme.spacing(1),
                            }}
                          >
                            <InputLabel
                              fullWidth
                              readOnly
                              margin="dense"
                              inputRef={register()}
                              name={`weekTotal.weekTotal.totalActivityCount`}
                              defaultValue={"0"}
                              inputProps={{
                                style: { color: "white", border: "unset" },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            className={classes.stickFooter}
                            align="center"
                            padding="none"
                          >
                            <InputLabel
                              fullWidth
                              readOnly
                              margin="dense"
                              inputRef={register()}
                              name={`weekTotal.weekTotal.totalHours`}
                              defaultValue={"0.0"}
                              className={classes.hrsField}
                              inputProps={{
                                style: { color: "white", border: "unset" },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            className={classes.stickFooter}
                            align="center"
                            padding="none"
                          >
                            <InputLabel
                              fullWidth
                              readOnly
                              margin="dense"
                              inputRef={register()}
                              className={classes.staffField}
                              name={`weekTotal.weekTotal.totalStaff`}
                              defaultValue={"0.0"}
                              inputProps={{
                                style: { color: "white", border: "unset" },
                              }}
                            />
                          </TableCell>
                        </React.Fragment>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
              <Grid
                container
                justify="space-between"
                spacing={2}
                style={{
                  marginTop: theme.spacing(2),
                  marginBottom: theme.spacing(2),
                }}
              >
                <Grid item xs={3}>
                  <Button
                    variant="outlined"
                    onClick={handleClear}
                    disabled={!clearAllowed || dscActivityEnable}
                  >
                    {t("other.btnClear")}
                  </Button>
                </Grid>

                <Grid item xs={6} container justify="flex-end" spacing={2}>
                  <Grid item>
                    <Button
                      color="primary"
                      variant="outlined"
                      disabled={!cancelAllowed || loading || dscActivityEnable}
                      onClick={handleCancel}
                    >
                      {t("pgPunch.btnCancel")}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      disabled={!saveAllowed || loading || !Number(productHours) || dscActivityEnable}
                    >
                      {t("pgAddWorker.btnSave")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </React.Fragment>
          )}

          {activities.length == 0 && !selections.buildings.length && (
            <Alert severity="info">{t("other.alertFirstLoad")}</Alert>
          )}
           {activities.length == 0 && selections.buildings.length>0 && !loading && (
            <Alert severity="info">{t("other.dscAlertAfterLoad")}</Alert>
          )}
        </form>
      </LoaderView>
      <ClearDialog
        onDialogClose={handleDialogClose}
        dialogOpen={clearDialogOpen}
      />
      <PlanDialog
        dialogOpen={planDialogOpen}
        onDialogClose={(e, reason) => {
          if (reason == "approved") {
            setDocId(selectedPlan.docId);
            loadPlan(selectedPlan);
          } else {
            setDocId(null);
            EventEmitter.dispatch("clearSelectedPlan", { prevPlan });
          }
          setPlanDialogOpen(false);
        }}
      />

      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "top", }}
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        autoHideDuration={500}
      >
        <Alert severity="success" variant="filled">
          Daily Schedule Control Saved Successfully!
        </Alert>
      </Snackbar>
    </div>
  );
}
